import axios from "axios";

export type SignatureUploadSettings = {
  spSiteUrl?: string;
  spDocumentLibrary?: string;
};

type PendingEntry = {
  promise: Promise<void>;
  dataUrl: string;
};

/** Module-level registry so uploads survive modal/component unmount. */
const pendingUploads = new Map<string, PendingEntry>();

/**
 * Keep a short-lived preview so remounting Edit can show the signature
 * even before SharePoint getFile succeeds.
 */
export function getSignaturePreviewDataUrl(sigId: string): string | undefined {
  return pendingUploads.get(sigId)?.dataUrl;
}

export function isSignatureUploadPending(sigId?: string | null): boolean {
  if (!sigId) return pendingUploads.size > 0;
  return pendingUploads.has(sigId);
}

export function hasPendingSignatureUploads(): boolean {
  return pendingUploads.size > 0;
}

/**
 * Start (or reuse) a background upload for this signature id.
 * Safe to call after the signing UI has unmounted.
 */
export function enqueueSignatureUpload(
  sigId: string,
  dataUrl: string,
  settings: SignatureUploadSettings = {}
): Promise<void> {
  if (!sigId || !dataUrl) {
    return Promise.resolve();
  }

  const existing = pendingUploads.get(sigId);
  if (existing) {
    return existing.promise;
  }

  const promise = (async () => {
    const reportFilesData = new FormData();
    reportFilesData.append("fileLocation", `_formData/${sigId}`);
    reportFilesData.append("spSiteUrl", settings.spSiteUrl || "");
    reportFilesData.append("spDocumentLibrary", settings.spDocumentLibrary || "");
    reportFilesData.append("file", dataUrl as any);

    await axios.post(
      `${process.env.REACT_APP_API_URL}/files/saveSignature`,
      reportFilesData,
      { withCredentials: true }
    );
  })()
    .catch((err) => {
      console.error(`[signatureUploadService] Failed to upload ${sigId}`, err);
      throw err;
    })
    .finally(() => {
      // Keep preview briefly for remount; drop pending flag
      const preview = pendingUploads.get(sigId)?.dataUrl;
      pendingUploads.delete(sigId);
      if (preview) {
        // Retain preview outside pending map for a few minutes via a simple cache
        previewCache.set(sigId, { dataUrl: preview, expires: Date.now() + 5 * 60 * 1000 });
      }
    });

  pendingUploads.set(sigId, { promise, dataUrl });
  return promise;
}

const previewCache = new Map<string, { dataUrl: string; expires: number }>();

export function getCachedSignaturePreview(sigId: string): string | undefined {
  const pending = pendingUploads.get(sigId)?.dataUrl;
  if (pending) return pending;
  const cached = previewCache.get(sigId);
  if (!cached) return undefined;
  if (cached.expires < Date.now()) {
    previewCache.delete(sigId);
    return undefined;
  }
  return cached.dataUrl;
}

/**
 * Await all in-flight signature uploads (e.g. before PDF generation).
 * Returns false if any upload is still pending after timeout.
 */
export async function waitForPendingSignatureUploads(
  timeoutMs = 120000
): Promise<boolean> {
  const start = Date.now();

  while (pendingUploads.size > 0) {
    if (Date.now() - start > timeoutMs) {
      return false;
    }
    const batch = Array.from(pendingUploads.values()).map((entry) => entry.promise);
    await Promise.allSettled(batch);
  }

  return true;
}

/** Test helper */
export function __resetSignatureUploadServiceForTests(): void {
  pendingUploads.clear();
  previewCache.clear();
  strokeFlushers.clear();
}

type StrokeFlusher = () => void;
const strokeFlushers = new Set<StrokeFlusher>();

/** Register a flusher so Add/Complete can commit debounced strokes before reading form values. */
export function registerSignatureStrokeFlusher(flusher: StrokeFlusher): () => void {
  strokeFlushers.add(flusher);
  return () => {
    strokeFlushers.delete(flusher);
  };
}

/** Flush any debounced signature strokes into form values + background upload queue. */
export function flushAllPendingSignatureStrokes(): void {
  Array.from(strokeFlushers).forEach((flusher) => {
    try {
      flusher();
    } catch (err) {
      console.error("[signatureUploadService] stroke flush failed", err);
    }
  });
}
